import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useMemo } from 'react';
import { loginRequest } from '../authConfig';
import { storage } from '../utils/browser';

export interface AuthUser {
  email: string;
  name: string;
}

/**
 * Single abstraction for MSAL in React components. Prefer this over importing `msalInstance` from authConfig.
 * The Axios client in `utils/api.ts` still uses `msalInstance` for interceptors (non-React context).
 */
export function useAuth() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] ?? undefined);

  const user: AuthUser | null = useMemo(() => {
    if (!account) return null;
    return { email: account.username, name: account.name ?? '' };
  }, [account]);

  const login = useCallback(async () => {
    const result = await instance.loginPopup(loginRequest);
    if (result.account) {
      instance.setActiveAccount(result.account);
    }
    return result;
  }, [instance]);

  const logout = useCallback(() => {
    instance.setActiveAccount(null);
    void instance.clearCache();
    storage.clearAll();
    sessionStorage.clear(); // also clear tab-scoped state on explicit logout
  }, [instance]);

  const getActiveUser = useCallback((): AuthUser | null => {
    const acct = instance.getActiveAccount();
    if (!acct) return null;
    return { email: acct.username, name: acct.name ?? '' };
  }, [instance]);

  return {
    isAuthenticated,
    user,
    account,
    login,
    logout,
    getActiveUser,
    msalInstance: instance,
  };
}
